import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { AuthService } from 'app/security/auth.service';
import { EventParamDTO } from 'app/header-footer-template/model/EventParamDTO';
import { EventService } from 'app/services/event-service';
import { CommonsText } from 'app/commons/CommonsText';

type EstadoSlug = 'en-curso' | 'abierto' | 'cerrado';

export interface ExpedienteCard {
    motivo: string;
    /** Descripcion del producto de la poliza tal y como la devuelve Innova (solo informativa). */
    producto: string;
    polizaNumero: string;
    expedienteNumero: string;
    eventYear: string;
    fechaOcurrencia: string;
    estadoRaw: string;
    estadoLabel: string;
    estadoSlug: EstadoSlug;
    /**
     * Si el NIF del asegurado del expediente (ASEGURADO de Innova) coincide con el del usuario
     * logado. Lo resuelve el backend en EventDTO.isInsuredInEvent, igual que hace el "Quiero..."
     * del historial de partes; aqui solo se transporta para decidir que acciones se ofrecen.
     */
    esAseguradoEnExpediente: boolean;
}

interface FilterOption {
    /** Valor que maneja el <select>: los codigos unidos por coma, o cadena vacia en "Todos". */
    value: string;
    /** Codigos crudos que agrupa la opcion; son los que viajan al backend. */
    ids: string[];
    label: string;
}

@Component({
    selector: 'app-expedientes-page',
    templateUrl: './expedientes-page.component.html',
    styleUrls: ['./expedientes-page.component.scss']
})
export class ExpedientesPageComponent implements OnInit, OnDestroy {

    /**
     * Parametro de la URL con el que otras pantallas piden
     * el listado filtrado por una poliza: /expedientes?policy=<numero>.
     */
    static readonly POLICY_QUERY_PARAM = 'policy';

    loading: boolean = false;
    response: any;
    cards: ExpedienteCard[] = [];

    // Solo el área directa (ONLYGAL) abre expedientes desde aquí: para reaseguro el alta ni
    // se ofrece, y de hecho el guard de rutas ya bloquea /events/register en ese caso.
    showDirectAreas: boolean = true;

    params = new EventParamDTO();

    filtersCollapsed: boolean = true;

    // Ambos desplegables se rellenan con los listados que devuelve el servicio (product y
    // statusType), que vienen calculados sobre todos los expedientes visibles del usuario, sin
    // los filtros de pantalla y sin paginar.
    seguros: FilterOption[] = [{ value: '', ids: [], label: 'Todos los seguros' }];
    estados: FilterOption[] = [{ value: '', ids: [], label: 'Todos los estados' }];

    // Numeros de expediente del usuario, que el servicio devuelve en eventItems (tambien
    // calculados antes de filtrar y paginar). Alimentan el multiselect, igual que en el filtro de
    // la pagina de historico.
    expedientesList: any[] = [];
    expedientesSeleccionados: any[] = [];
    expedientesSettings: any = {};

    // Mensaje del control de coherencia entre las dos fechas.
    fechaError: string = '';

    selectedSeguro: string = '';
    selectedEstado: string = '';
    fechaDesde: string = '';
    fechaHasta: string = '';

    // Poliza por la que llega filtrado el listado desde la URL (?policy=). No tiene campo en el
    // panel de filtros: se muestra como una chip que permite quitarlo.
    polizaFiltro: string = null;

    private queryParamsSub: Subscription;

    constructor(private router: Router,
                private route: ActivatedRoute,
                private authService: AuthService,
                private eventService: EventService,
                private toastr: ToastrService) {}

    ngOnInit(): void {
        this.showDirectAreas = this.authService.getSelectedEntity() === 'ONLYGAL';

        this.params.pageOffset = 0;
        this.params.pageSize = 12;
        this.params.statusType = [];
        this.params.product = [];
        this.params.eventItems = [];

        // Mismos ajustes que el multiselect de partes de event-filter, para que se comporte igual
        // (buscador interno, sin "seleccionar todo" y sin boton de limpiar).
        this.expedientesSettings = {
            singleSelection: false,
            text: 'Todos los expedientes',
            enableCheckAll: false,
            enableSearchFilter: true,
            enableFilterSelectAll: false,
            searchPlaceholderText: 'Nº expediente',
            classes: 'multi-select',
            clearAll: false,
            // La libreria si lee maxHeight (al contrario que tagToBody), asi que el alto del panel
            // se limita por ajuste y no por CSS.
            maxHeight: 220,
            // Sin chips. La plantilla de la libreria oculta cada una con
            // [hidden]="k > settings.badgeShowLimit-1", asi que con 0 no se pinta ninguna;
            // badgeShowLimit si lo lee de verdad, al contrario que tagToBody. Se quitan porque
            // eran lo unico que hacia crecer el campo de alto: al no caber en la linea se
            // apilaban y el tercer filtro dejaba de estar alineado con los dos de al lado (con
            // 29 expedientes, que es lo que tiene un usuario real, pasaba de 50 a casi 300px).
            // Lo seleccionado se resume en texto en la plantilla.
            badgeShowLimit: 0
        };
        // Filtro fijo: el listado solo muestra los expedientes de polizas en las que el
        // usuario es tomador. No es un filtro de pantalla, no se expone en el formulario.
        //this.params.isTaker = true;

        // La busqueda inicial la lanza la primera emision. Se escucha queryParamMap y no el
        // snapshot porque Angular reutiliza el componente al navegar a /expedientes con otros
        // parametros (por ejemplo al quitar la chip de poliza), y entonces ngOnInit no se repite.
        this.queryParamsSub = this.route.queryParamMap.subscribe((query: ParamMap) => {
            this.polizaFiltro = this.toPolicyNumber(query.get(ExpedientesPageComponent.POLICY_QUERY_PARAM));
            this.params.policyNumber = this.polizaFiltro;
            this.params.pageOffset = 0;
            this.search();
        });
    }

    ngOnDestroy(): void {
        if (this.queryParamsSub) {
            this.queryParamsSub.unsubscribe();
        }
    }

    /**
     * El backend recibe el numero de poliza como Long (SearchDTO.policyNumber): cualquier valor
     * que no sean solo digitos devolveria 400, asi que se descarta y se lista sin filtrar.
     */
    private toPolicyNumber(valor: string): string {
        const limpio = (valor || '').trim();
        return /^\d+$/.test(limpio) ? limpio : null;
    }

    /**
     * Quita el filtro de poliza navegando a la misma ruta sin el parametro, para que la URL y el
     * listado no se contradigan; la suscripcion a queryParamMap relanza la busqueda.
     */
    quitarFiltroPoliza(): void {
        this.router.navigate(['/expedientes']);
    }

    aplicarFiltros(): void {
        // Estado y seguro los resuelve el backend sobre todos los expedientes del usuario, no
        // sobre las tarjetas de la pagina actual: aqui solo se traduce la seleccion a la forma que
        // espera el servicio. Se vuelve a la primera pagina porque al filtrar cambia el total y el
        // offset anterior puede quedarse fuera del nuevo resultado.
        if (!this.rangoFechasValido()) {
            return;
        }

        this.params.statusType = this.toParamItems(this.estados, this.selectedEstado);
        this.params.product = this.toParamItems(this.seguros, this.selectedSeguro);
        this.params.eventItems = this.expedientesSeleccionados || [];
        this.params.pageOffset = 0;
        // El filtro de poliza que llega por la URL se mantiene hasta que se quita su chip.
        this.params.policyNumber = this.polizaFiltro;
        this.params.startDate = this.toBackendDate(this.fechaDesde);
        this.params.endDate = this.toBackendDate(this.fechaHasta);
        this.search();
    }

    /**
     * El equivalente a comprobarDesde/comprobarHasta de event-filter: alli son validadores del
     * formulario reactivo, y aqui el panel trabaja con ngModel suelto, asi que la coherencia se
     * comprueba al aplicar. Las fechas vienen del input nativo en formato ISO (yyyy-MM-dd), que
     * se ordena igual alfabeticamente que cronologicamente, de ahi la comparacion directa.
     */
    private rangoFechasValido(): boolean {
        if (this.fechaDesde && this.fechaHasta && this.fechaDesde > this.fechaHasta) {
            this.fechaError = 'La fecha "desde" no puede ser posterior a la fecha "hasta".';
            return false;
        }
        this.fechaError = '';
        return true;
    }

    /**
     * El input nativo de tipo date entrega yyyy-MM-dd, pero SearchDTO declara las fechas con
     * pattern "dd-MM-yyyy" (que tiene prioridad sobre el iso de la misma anotacion), asi que
     * mandarlas como las da el navegador devuelve 400 y el filtro de fechas no llegaba a aplicarse.
     */
    private toBackendDate(iso: string): string {
        if (!iso) { return null; }
        const partes = iso.split('-');
        if (partes.length !== 3) { return null; }
        return partes[2] + '-' + partes[1] + '-' + partes[0];
    }

    /**
     * Traduce la opcion elegida a lo que serializa event-service: un array de objetos con id, del
     * que se manda un parametro por elemento. "Todos" devuelve un array vacio, que deja el filtro
     * sin informar y llega al backend como null (su forma de decir "sin filtrar"); mandar el
     * conjunto vacio no valdria, porque alli se comprueba con contains() y vaciaria el listado.
     */
    private toParamItems(opciones: FilterOption[], valor: string): any[] {
        const elegida = opciones.filter(o => o.value === valor)[0];
        if (!elegida || elegida.ids.length === 0) { return []; }
        return elegida.ids.map(id => ({ id: id }));
    }

    /**
     * Lo que se pinta sobre el campo cerrado en lugar de las chips de la libreria. Con un solo
     * expediente se enseña su numero, que es justo lo que el usuario estaba buscando; a partir de
     * dos solo cabe el recuento, porque el campo mide una linea y el usuario puede tener decenas
     * (29 en el caso que destapo el problema). Que expedientes concretos estan marcados se
     * consulta abriendo el desplegable, que ademas lleva buscador.
     */
    get resumenExpedientes(): string {
        const elegidos = this.expedientesSeleccionados || [];
        if (elegidos.length === 0) { return ''; }
        if (elegidos.length === 1) {
            const uno = elegidos[0] || {};
            // itemName es la etiqueta por defecto de la libreria (no se informa labelKey).
            return 'Expediente ' + (uno.itemName || uno.id || '');
        }
        return elegidos.length + ' expedientes seleccionados';
    }

    /**
     * Deshacer la seleccion desde el desplegable serian tantos clics como expedientes marcados,
     * asi que el campo ofrece un aspa para vaciarlo de una vez. Solo cambia lo que se ve: la
     * busqueda no se relanza hasta que se pulsa "Aplicar filtros", igual que al cambiar
     * cualquiera de los dos desplegables de al lado.
     */
    limpiarExpedientes(evento: Event): void {
        // Sin esto el clic llegaria al boton de la libreria y abriria el desplegable.
        evento.stopPropagation();
        this.expedientesSeleccionados = [];
    }

    toggleFilters(): void {
        this.filtersCollapsed = !this.filtersCollapsed;
    }

    irNuevoExpediente(): void {
        this.router.navigate(['/events/register']);
    }

    /**
     * El detalle y la mensajeria del expediente solo se ofrecen cuando el asegurado del
     * expediente es el propio usuario logado. El listado puede traer partes de polizas en las que
     * el usuario es tomador pero el asegurado es otra persona (un hijo, un empleado...), y en ese
     * caso no debe poder entrar al detalle ni escribir al tramitador.
     *
     * Ante la duda (dato ausente) se oculta: es preferible que un tomador legitimo tenga que
     * llamar a que alguien vea un expediente de otra persona.
     */
    puedeOperar(card: ExpedienteCard): boolean {
        return !!card && card.esAseguradoEnExpediente === true;
    }

    irDetalle(card: ExpedienteCard): void {
        if (!this.puedeOperar(card)) { return; }
        const ruta = this.eventRoute(card);
        if (ruta) { this.router.navigate(ruta); }
    }

    irMensajeria(card: ExpedienteCard): void {
        if (!this.puedeOperar(card)) { return; }
        const ruta = this.eventRoute(card);
        if (ruta) { this.router.navigate(ruta.concat(['messenger'])); }
    }

    private eventRoute(card: ExpedienteCard): any[] | null {
        const ref = (card && card.expedienteNumero || '').toString().trim();
        if (!ref) { return null; }
        return ['/event', ref];
    }

    irChat(): void {
        this.router.navigate(['/contact/form']);
    }

    /**
     * Ya no queda ningun filtro de pantalla: estado, seguro y numero de expediente los aplica el
     * backend sobre todos los expedientes del usuario, porque filtrando aqui solo se miraban las
     * tarjetas de la pagina actual. Se mantiene la propiedad para no tocar la plantilla.
     */
    get visibleCards(): ExpedienteCard[] {
        return this.cards;
    }

    private search(): void {
        this.loading = true;
        this.eventService.search(this.params)
            .then(success => {
                this.loading = false;
                this.response = success;
                this.cards = this.toCards(success);
                this.refreshFilters(success);
            })
            .catch(() => {
                this.loading = false;
                this.response = null;
                this.cards = [];
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_PARTES,
                                  CommonsText.PROBLEMA_DE_CONECTIVIDAD,
                                  { timeOut: 5000 });
            });
    }

    private toCards(resp: any): ExpedienteCard[] {
        const content = (resp && resp.content) ? resp.content : [];
        return content.map(item => {
            const estadoRaw = (item.eventStatus || '').toString();
            const estadoSlug = this.estadoToSlug(estadoRaw);
            return {
                motivo: item.versionType || '',
                producto: item.policyProduct || '',
                polizaNumero: (item.policyNumber || item.idRea || '').toString(),
                expedienteNumero: (item.eventNumber || '').toString(),
                eventYear: (item.eventYear || '').toString(),
                fechaOcurrencia: this.formatDay(item.eventEndDate || item.eventDate),
                estadoRaw,
                estadoLabel: this.estadoToLabel(estadoSlug, estadoRaw),
                estadoSlug,
                // Se compara estrictamente con true: si el backend no mandase el dato (undefined)
                // no se puede afirmar que el asegurado sea el usuario, y se oculta.
                esAseguradoEnExpediente: item.isInsuredInEvent === true
            } as ExpedienteCard;
        });
    }

    get currentPage(): number {
        return this.response ? this.response.number + 1 : 1;
    }

    /**
     * Numeros a pintar en el paginado. Con mas de 7 paginas se recorta alrededor de la actual y
     * se marcan los saltos con null, que la plantilla dibuja como puntos suspensivos. Misma
     * logica que el listado de recibos.
     */
    getPages(): (number | null)[] {
        if (!this.response || !this.response.totalPages) { return []; }
        const total = this.response.totalPages;
        const current = this.currentPage;

        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        let start = Math.max(1, current - 2);
        let end = Math.min(total, current + 2);

        if (end - start < 4) {
            if (start === 1) { end = Math.min(total, 5); }
            else { start = Math.max(1, end - 4); }
        }

        const pages: (number | null)[] = [];

        if (start > 1) {
            pages.push(1);
            if (start > 2) { pages.push(null); }
        }

        for (let i = start; i <= end; i++) { pages.push(i); }

        if (end < total) {
            if (end < total - 1) { pages.push(null); }
            pages.push(total);
        }

        return pages;
    }

    prev(): void {
        if (!this.loading && this.response && !this.response.first) {
            this.irAPagina(this.response.number - 1);
        }
    }

    next(): void {
        if (!this.loading && this.response && !this.response.last) {
            this.irAPagina(this.response.number + 1);
        }
    }

    goToPage(p: number): void {
        if (!this.loading) {
            this.irAPagina(p - 1);
        }
    }

    /**
     * A diferencia de recibos, que pasa por su servicio, aqui la pagina es un parametro mas de
     * la busqueda: se fija el offset y se vuelve a pedir.
     */
    private irAPagina(offset: number): void {
        this.params.pageOffset = offset;
        this.search();
    }

    private refreshFilters(resp: any): void {
        this.seguros = this.toOptions(resp && resp.product, 'Todos los seguros');
        this.estados = this.toOptions(resp && resp.statusType, 'Todos los estados');

        // El listado de expedientes solo se refresca cuando viene informado: al filtrar por uno
        // concreto el servicio los sigue devolviendo todos, pero si alguna respuesta no lo trajera
        // se conserva el ultimo bueno en vez de dejar el multiselect vacio y sin poder deshacer.
        const items = (resp && resp.eventItems) ? resp.eventItems : [];
        if (items.length > 0) {
            this.expedientesList = items;
        }

        // Las opciones se rehacen en cada busqueda, pero el servicio las calcula antes de aplicar
        // los filtros, asi que son siempre las mismas y la seleccion del usuario se conserva. Si
        // aun asi desapareciera, se vuelve a "Todos" para no dejar el desplegable en blanco; se
        // limpia tambien el parametro para que lo que se ve y lo que se pide no se contradigan.
        if (!this.existeOpcion(this.seguros, this.selectedSeguro)) {
            this.selectedSeguro = '';
            this.params.product = [];
        }
        if (!this.existeOpcion(this.estados, this.selectedEstado)) {
            this.selectedEstado = '';
            this.params.statusType = [];
        }
    }

    private existeOpcion(opciones: FilterOption[], valor: string): boolean {
        return opciones.some(o => o.value === valor);
    }

    /**
     * Convierte un listado del servicio ({id, itemName}) en opciones del desplegable: el codigo
     * como valor y la descripcion como etiqueta. El valor tiene que ser el codigo porque ahora
     * filtra el backend, y alli se compara contra el codigo crudo del parte (eventStatus /
     * policyProductId), no contra la descripcion que llevan las tarjetas. Varios codigos pueden
     * compartir descripcion (modalidades del mismo producto), asi que se agrupan en una sola
     * opcion que los manda todos y el desplegable sigue mostrando un unico seguro por nombre.
     */
    private toOptions(items: any[], etiquetaTodos: string): FilterOption[] {
        const porEtiqueta: { [label: string]: string[] } = {};
        (items || []).forEach(i => {
            const id = (i && (null !== i.id) && (undefined !== i.id) ? i.id : '').toString().trim();
            if (!id) { return; }
            // Si la descripcion no viene resuelta se usa el propio codigo como etiqueta: queda
            // feo, pero peor seria dejar fuera del filtro expedientes que el usuario si ve.
            const label = ((i.itemName ? i.itemName : '').toString().trim()) || id;
            if (porEtiqueta[label]) { porEtiqueta[label].push(id); } else { porEtiqueta[label] = [id]; }
        });

        const opts: FilterOption[] = [{ value: '', ids: [], label: etiquetaTodos }];
        Object.keys(porEtiqueta).sort().forEach(label => {
            const ids = porEtiqueta[label].sort();
            opts.push({ value: ids.join(','), ids: ids, label: label });
        });
        return opts;
    }

    private estadoToSlug(raw: string): EstadoSlug {
        const normalized = raw.toLowerCase();
        if (normalized.indexOf('curso') !== -1 || normalized.indexOf('tramit') !== -1) {
            return 'en-curso';
        }
        if (normalized.indexOf('cerrad') !== -1 || normalized.indexOf('finaliz') !== -1) {
            return 'cerrado';
        }
        return 'abierto';
    }

    private estadoToLabel(slug: EstadoSlug, raw: string): string {
        if (raw && raw.trim().length > 0) {
            return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
        }
        switch (slug) {
            case 'en-curso': return 'En curso';
            case 'cerrado':  return 'Cerrado';
            default:         return 'Abierto';
        }
    }

    private formatDay(value: any): string {
        if (!value) { return ''; }
        const str = value.toString();
        const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) {
            return iso[3] + '/' + iso[2] + '/' + iso[1];
        }
        const date = new Date(str);
        if (isNaN(date.getTime())) { return str; }
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear();
    }
}
